import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { fmtDateShort } from "@/lib/format";

export function AssignTurnoDialog({ target, onClose, onSaved }) {
  const [f, setF] = useState({ mansione_id: "", start_time: "", end_time: "", note: "" });
  const [conflict, setConflict] = useState(null);
  const [saving, setSaving] = useState(false);
  const upd = (patch) => { setF((p) => ({ ...p, ...patch })); setConflict(null); };
  const { data: mansioni = [] } = useQuery({
    queryKey: ["mansioni-active"],
    queryFn: async () => (await api.get("/mansioni-area/mansioni", { params: { only_active: true } })).data,
    enabled: !!target,
  });
  useEffect(() => {
    if (target) { setF({ mansione_id: "", start_time: target.start_time || "", end_time: target.end_time || "", note: "" }); setConflict(null); }
  }, [target]);

  async function save(force = false) {
    if (!f.mansione_id) return toast.error("Scegli la mansione");
    setSaving(true);
    try {
      await api.post("/mansioni-area/turni", { ...f, start_time: f.start_time || null, end_time: f.end_time || null,
        student_id: target.student.id, date: target.date, force });
      toast.success(`${target.student.name} assegnato/a`);
      onSaved(); onClose();
    } catch (e) {
      if (e.response?.status === 409) {
        const raw = e.response.data.detail_raw || {};
        const list = (raw.conflicts || []).slice(0, 4).map((c) => `${c.title}${c.start_time ? ` ${c.start_time}${c.end_time ? `–${c.end_time}` : ""}` : ""}`);
        setConflict(`${raw.message || e.response.data.detail || "Conflitto con altri impegni"}${list.length ? ` (${list.join(", ")})` : ""}`);
      }
      else toast.error(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  }

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="assign-turno-dialog">
        <DialogHeader>
          <DialogTitle>Assegna a una mansione</DialogTitle>
          {target && <div className="text-sm text-stone-500">{target.student.name} · {fmtDateShort(target.date)}</div>}
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Mansione</Label>
            <Select value={f.mansione_id} onValueChange={(v) => upd({ mansione_id: v })}>
              <SelectTrigger data-testid="assign-mansione"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
              <SelectContent>{mansioni.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Dalle</Label><Input type="time" value={f.start_time} onChange={(e) => upd({ start_time: e.target.value })} data-testid="assign-start"/></div>
            <div><Label className="text-xs">Alle</Label><Input type="time" value={f.end_time} onChange={(e) => upd({ end_time: e.target.value })} data-testid="assign-end"/></div>
          </div>
          <div><Label className="text-xs">Nota (opz.)</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Es. servizio straordinario" data-testid="assign-note"/></div>
          {conflict && <div className="text-[12px] rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2" data-testid="assign-conflict">{conflict}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="assign-cancel">Annulla</Button>
          {conflict
            ? <Button onClick={() => save(true)} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="assign-force">Assegna comunque</Button>
            : <Button onClick={() => save(false)} disabled={saving} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="assign-save">Assegna</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
